import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getPayment } from '../../redux/actions/payment';
import { Spin } from 'antd';
import Button from '../../components/Button';
import moment from 'moment';
import { DATE_FORMAT } from '../../utils/constants';
import './Payment.scss'
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const Payment = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isSuccessPayment, isLoading, order, payment } = useSelector(
    (state) => state.payment
  );
  const [responseGetaway, setResponseGetaway] = useState(null); // Added state for responseGetaway
  const [showSummary, setShowSummary] = useState(false);

  const query = useQuery();

  // Fetch payment details
  const checkPayment = (referenceNumber, queryToken) => {
    dispatch(getPayment({ referenceNumber, queryToken }));
   // console.log("Payment request sent", referenceNumber, queryToken);
  };

  const queryOrder = query.get('order_no');
  const queryReference = query.get('reference_number');
  const queryToken = query.get('token');

  useEffect(() => {
    if (queryOrder && queryReference) {
      checkPayment(queryReference, queryToken);
    }
  }, [queryOrder, queryReference]);

  // Handle gateway response
  useEffect(() => {
    if (payment?.gatewayResponse) {
      try {
        const parsedResponse = JSON.parse(payment.gatewayResponse);
        //console.log(parsedResponse); // Log the parsed response
        setResponseGetaway(parsedResponse); // Update the responseGetaway state
      } catch (error) {
        console.error("Error parsing payment gateway response:", error);
      }
    }
  }, [payment]); // Re-run whenever the 'payment' state changes

  // Delay showing payment summary by 2 seconds
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setShowSummary(true);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      setShowSummary(false);
    }
  }, [isLoading]);

  return queryToken ? (
    <div className="payment-page">
      {isLoading || !showSummary ? (
        <Spin spinning={true} />
      ) : isSuccessPayment ? (
        <div className="confirmation">
          <div className="header">
            <img src="/logo.png" alt="Abraaj Logo" className="header__logo" />
            <h1 className="header__title">{t('payment.order successful')}</h1>
            <p className="header__subtitle">{t('payment.thank you for shopping with us')}</p>
          </div>

          <div className="details">
            <h2 className="details__title">{t('payment.order summary')}</h2>

            <div className="details__list">
              <div className="details__item">
                <div className="label">{t('payment.date of Order')}</div>
                <div className="value">{moment(payment?.createdAt).format('YYYY-MM-DD')}</div>
              </div>

              <div className="details__item">
                <div className="label">{t('payment.order number')}</div>
                <div className="value">{payment?.orderNumber}</div>
              </div>

              <div className="details__item">
                <div className="label">{t('payment.name')}</div>
                <div className="value">{order?.fullName}</div>
              </div>

              <div className="details__item">
                <div className="label">{t('payment.mobile number')}</div>
                <div className="value">{order?.phoneNumber}</div>
              </div>

              <div className="details__item">
                <div className="label">{t('payment.total amount')}</div>
                <div className="value">{order?.subTotalAmount}</div>
              </div>

              <div className="details__item">
                <div className="label">{t('payment.payment type')}</div>
                <div className="value">{order?.paymentMethod}</div>
              </div>

              <div className="details__item">
                <div className="label">{t('payment.payment status')}</div>
                <div className="value">{order?.paymentStatus}</div>
              </div>

              <div className="details__item">
                <div className="label">Payment ID</div>
                <div className="value">
                  {responseGetaway?.paymentid || responseGetaway?.gateway_response?.paymentid}
                </div>
              </div>

              <div className="details__item">
                <div className="label">Auth. Code</div>
                <div className="value">
                  {responseGetaway?.auth || responseGetaway?.gateway_response?.auth}
                </div>
              </div>

              <div className="details__item">
                <div className="label">Track ID</div>
                <div className="value">
                  {responseGetaway?.trackid || responseGetaway?.gateway_response?.trackid}
                </div>
              </div>

              <div className="details__item">
                <div className="label">{t('payment.date of delivery')}</div>
                <div className="value value--long">
                  Your order will be delivered within 48 hours
                </div>
              </div>

              <div className="details__item">
                <div className="label">{t('payment.delivery address')}</div>
                <div className="value value--long">{`${order?.deliveryAddress?.area?.name},${order?.deliveryAddress?.block},${order?.deliveryAddress?.street},${order?.deliveryAddress?.houseBuildingNumber}`}</div>
              </div>
            </div>
          </div>

          <div className="button">
            <button className="button__shop">{t('payment.go to shop')}</button>
          </div>
        </div>
      ) : (
        <div className="error">
          <p className="error__message">
            {t('payment.status')} : {t('notifs.error title')}
          </p>
        </div>
      )}
    </div>
  ) : (
    <div className="paymentPage">
      {isLoading || !showSummary ? <Spin spinning={true} /> : 
      <div className="">
        {showSummary && isSuccessPayment && responseGetaway ? (
          <>
            <h3>{t('payment.order successful')}</h3>
            <p>{t('payment.thank you for shopping with us')}</p>
            <p className="caption-text">{t('payment.order summary')}</p>
            <p>
              {t('payment.date of Order')}:{moment(payment?.createdAt).format(DATE_FORMAT)}
            </p>
            <p>
              {t('payment.order number')}: {payment?.orderNumber}
            </p>
            <p>
              {t('payment.name')}: {order?.fullName}
            </p>
            <p>
              {t('payment.mobile number')}: {order?.phoneNumber}
            </p>
            <p>
              {t('payment.total amount')}: {order?.subTotalAmount}
            </p>
            <p>
              {t('payment.payment type')}: {order?.paymentMethod}
            </p>
            <p>
              {t('payment.payment status')}: {order?.paymentStatus}
            </p>
            <p>
              Payment ID:{' '}
              {responseGetaway?.paymentid || responseGetaway?.gateway_response?.paymentid}
            </p>
            <p>Auth. Code: {responseGetaway?.auth || responseGetaway?.gateway_response?.auth}</p>
            <p>
              Track ID: {responseGetaway?.trackid || responseGetaway?.gateway_response?.trackid}
            </p>
            <p>{t('payment.date of delivery')}: Your order will be delivered within 48 hours</p>
            <p>{t('payment.delivery address')}:</p>
            <Button className="notFound-btn">{t('payment.go to shop')}</Button>
          </>
        ) : (
          <p>
            {t('payment.status')} : {t('notifs.error title')}
          </p>
        )}
      </div>
      }
      
    </div>
  )
};

export default Payment;
